/**
 * CharacterScene.ts — Character sheet overlay.
 *
 * Tabs: Stats, Inventory & Equipment, Traits, Relationships, Quests, Save Game.
 * Accessed from StoryScene via "0) Character Sheet".
 *
 * Migrated to FrameRenderer + PanelWriter for framed UI layout.
 */

import type { GameEngine, Scene } from '@core/engine/GameEngine';
import type { MenuOption } from '@ui/MenuSystem';
import type { StatName } from '@game/data/types';
import type { GameState } from '@core/engine/GameState';
import type { TooltipDefinition } from '@ui/TooltipSystem';
import { getClass } from '@game/data/classes';
import { getItem } from '@game/data/items';
import { getNPC } from '@game/data/npcs';
import { MAX_SAVE_SLOTS } from '@core/engine/SaveLoad';
import { TitleScene } from './TitleScene';
import { SkillTreeScene } from './SkillTreeScene';
import { FactionScene } from './FactionScene';

const STAT_NAMES: StatName[] = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'];

const STAT_ABBR: Record<StatName, string> = {
  strength: 'STR',
  dexterity: 'DEX',
  constitution: 'CON',
  intelligence: 'INT',
  wisdom: 'WIS',
  charisma: 'CHA',
};

const STAT_TOOLTIPS: Record<StatName, TooltipDefinition> = {
  strength: {
    subject: 'Strength (STR)',
    description: [
      'Physical power and melee prowess.',
      'Used for combat checks, forcing doors, and intimidation.',
    ],
    effect: 'Higher STR increases melee damage and physical challenge success',
    color: 'frostBlue',
  },
  dexterity: {
    subject: 'Dexterity (DEX)',
    description: [
      'Agility, reflexes, and finesse.',
      'Used for dodging, stealth, and precision tasks.',
    ],
    effect: 'Higher DEX improves evasion and ranged accuracy',
    color: 'frostBlue',
  },
  constitution: {
    subject: 'Constitution (CON)',
    description: [
      'Endurance and physical resilience.',
      'Determines maximum HP and resistance to frostbite.',
    ],
    effect: 'Higher CON grants more HP and slower frostbite accumulation',
    color: 'frostBlue',
  },
  intelligence: {
    subject: 'Intelligence (INT)',
    description: [
      'Knowledge, memory, and magical aptitude.',
      'Used for arcane checks, lore, and spell effectiveness.',
    ],
    effect: 'Higher INT improves spell power and puzzle-solving',
    color: 'frostBlue',
  },
  wisdom: {
    subject: 'Wisdom (WIS)',
    description: [
      'Perception, intuition, and willpower.',
      'Used for detecting danger, resisting corruption, and insight.',
    ],
    effect: 'Higher WIS improves corruption resistance and NPC reading',
    color: 'frostBlue',
  },
  charisma: {
    subject: 'Charisma (CHA)',
    description: [
      'Force of personality and social influence.',
      'Used for persuasion, deception, and leadership.',
    ],
    effect: 'Higher CHA improves dialogue options and NPC reactions',
    color: 'frostBlue',
  },
};

const VITALS_TOOLTIP: TooltipDefinition = {
  subject: 'Vitals',
  description: [
    'HP: Hit Points \u2014 your health. Reaching 0 HP is fatal.',
    'Frostbite: Exposure to cold. At 100, you freeze to death.',
    'Corruption: Dark influence. At 100, you become lost to darkness.',
  ],
  effect: 'CON reduces frostbite gain. WIS reduces corruption gain.',
  color: 'frostBlue',
};

const RELATIONSHIP_TIER_TOOLTIP: TooltipDefinition = {
  subject: 'Relationship Tiers',
  description: [
    'Allied (80-100): NPC will risk themselves for you',
    'Friendly (50-79): NPC trusts and aids you',
    'Neutral (20-49): NPC is indifferent',
    'Distrustful (10-19): NPC suspects your motives',
    'Hostile (0-9): NPC opposes you actively',
  ],
  effect: 'Higher relationships unlock special dialogue and assistance',
  color: 'frostBlue',
};

export class CharacterScene implements Scene {
  name = 'CharacterScene';

  async enter(engine: GameEngine): Promise<void> {
    const { gameState } = engine.getContext();
    if (!gameState) {
      await engine.popScene();
      return;
    }

    await this.mainMenu(engine, gameState);
  }

  async exit(engine: GameEngine): Promise<void> {
    engine.getContext().sprites.hide();
  }

  // ─── Main Menu ───────────────────────────────────────────

  private async mainMenu(engine: GameEngine, state: GameState): Promise<void> {
    const ctx = engine.getContext();
    const { menu, frameRenderer, effects, terminalFrame } = ctx;
    const cls = getClass(state.character.classId);

    // Update terminal frame
    terminalFrame.updateInfo({
      chapter: 'CHARACTER',
      location: state.character.name,
    });

    while (true) {
      ctx.sprites.hide();
      const footer = `HP: ${state.character.hp}/${state.character.hpMax}  |  Frost: ${state.character.frostbite}/100  |  Gold: ${state.gold}`;

      const layout = frameRenderer.drawFrame({
        title: 'CHARACTER',
        headerRight: `${state.character.name} \u2014 ${cls.name} Lv.${state.character.level}`,
        footer,
        borderColor: 'frostBlue',
      });

      const writer = frameRenderer.createWriter(layout.content, effects);
      writer.blankLine();
      writer.writeCentered('\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550', 'runeGold');
      writer.writeCentered(`${state.character.name} \u2014 ${cls.name}  Lv.${state.character.level}`, 'icyWhite');
      writer.writeCentered('\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550', 'runeGold');
      writer.blankLine();

      const options: MenuOption[] = [
        { label: 'Stats', color: 'amber' },
        { label: 'Skill Tree', color: 'amber' },
        { label: 'Inventory & Equipment', color: 'amber' },
        { label: 'Traits', color: 'amber' },
        { label: 'Relationships', color: 'amber' },
        { label: 'Quests', color: 'amber' },
        { label: 'Factions', color: 'amber' },
        { label: 'Save Game', color: 'amber' },
        { label: 'Save & Quit to Title', color: 'red' },
      ];

      const menuCfg = writer.menuConfig();
      const selection = await menu.show(options, {
        showExit: true,
        exitLabel: 'Back',
        startRow: menuCfg.startRow,
        startCol: menuCfg.startCol,
        maxWidth: menuCfg.maxWidth,
        onBeforeRender: () => {
          ctx.sprites.hide();
          frameRenderer.drawFrame({
            title: 'CHARACTER',
            headerRight: `${state.character.name} \u2014 ${cls.name} Lv.${state.character.level}`,
            footer,
            borderColor: 'frostBlue',
          });
        },
      });

      switch (selection) {
        case 0: await this.showStats(engine, state); break;
        case 1: await engine.pushScene(new SkillTreeScene()); break;
        case 2: await this.showInventory(engine, state); break;
        case 3: await this.showTraits(engine, state); break;
        case 4: await this.showRelationships(engine, state); break;
        case 5: await this.showQuests(engine, state); break;
        case 6: await engine.pushScene(new FactionScene()); break;
        case 7: await this.showSaveMenu(engine, state); break;
        case 8: {
          const confirmed = await ctx.prompt.confirm('Save and quit to title screen?', false);
          if (confirmed) {
            const node = ctx.storyEngine.getCurrentNode();
            const cls2 = getClass(state.character.classId);
            ctx.saveSystem.save(0, state, node?.title ?? '', cls2.name);

            // Show save confirmation in frame
            ctx.sprites.hide();
            const saveLayout = frameRenderer.drawFrame({
              title: 'SAVING...',
              borderColor: 'frostBlue',
            });
            const saveWriter = frameRenderer.createWriter(saveLayout.content, effects);
            const offset = saveWriter.calculateVerticalOffset(3);
            saveWriter.skip(offset);
            saveWriter.writeCentered('Game saved.', 'green');
            await effects.delay(1000);

            ctx.gameState = null;
            await engine.resetTo(new TitleScene());
            return;
          }
          break;
        }
        case -1:
          await engine.popScene();
          return;
      }
    }
  }

  // ─── Stats Tab ───────────────────────────────────────────

  private async showStats(engine: GameEngine, state: GameState): Promise<void> {
    const ctx = engine.getContext();
    const { menu, effects, characterSystem, frameRenderer } = ctx;

    while (true) {
      ctx.sprites.hide();
      const xpNeeded = characterSystem.getXPForNextLevel(state);
      const footer = `Level ${state.character.level}  |  XP: ${state.character.xp}${xpNeeded !== undefined ? `/${xpNeeded}` : ' MAX'}  |  Gold: ${state.gold}`;

      const layout = frameRenderer.drawFrame({
        title: 'STATS',
        headerRight: `Lv.${state.character.level}`,
        footer,
        borderColor: 'frostBlue',
      });

      const writer = frameRenderer.createWriter(layout.content, effects);
      writer.blankLine();
      writer.writeCentered('═══ ATTRIBUTES ═══', 'runeGold');
      writer.blankLine();

      // Main stats
      for (const stat of STAT_NAMES) {
        const base = state.character.stats[stat];
        const effective = state.getEffectiveStat(stat);
        const bonus = effective - base;
        const modifier = Math.floor((effective - 10) / 2);
        const modStr = modifier >= 0 ? `+${modifier}` : `${modifier}`;

        writer.beginLine();
        writer.write(`  ${STAT_ABBR[stat]}  ${effective}`, 'white');
        writer.write(` [${modStr}]`, modifier > 0 ? 'green' : modifier < 0 ? 'red' : 'frostSilver');
        if (bonus !== 0) {
          writer.write(` (${bonus > 0 ? '+' : ''}${bonus} equip)`, 'frostSilver');
        }
        writer.endLine();
      }

      writer.blankLine();
      writer.writeCentered('═══ VITALS ═══', 'runeGold');
      writer.blankLine();

      // HP, Frostbite, Corruption
      const hpRatio = state.character.hp / state.character.hpMax;
      const hpColor = hpRatio > 0.5 ? 'green' : hpRatio > 0.25 ? 'yellow' : 'red';
      writer.writeLine(`  HP:         ${state.character.hp}/${state.character.hpMax}`, hpColor);
      writer.writeLine(`  Frostbite:  ${state.character.frostbite}/100`, 'frostbiteCyan');
      writer.writeLine(`  Corruption: ${state.character.corruption}/100`, 'magenta');

      writer.blankLine();
      writer.writeCentered('═══ PROGRESSION ═══', 'runeGold');
      writer.blankLine();

      // XP bar
      const xpProgress = characterSystem.getXPProgress(state);
      if (xpNeeded !== undefined) {
        const barWidth = 20;
        const filled = Math.round(xpProgress * barWidth);
        const bar = '\u2588'.repeat(filled) + '\u2591'.repeat(barWidth - filled);
        writer.writeLine(`  XP: ${state.character.xp}/${xpNeeded}  [${bar}]`, 'yellow');
      } else {
        writer.writeLine(`  XP: ${state.character.xp} (MAX LEVEL)`, 'yellow');
      }

      writer.writeLine(`  Gold: ${state.gold}`, 'yellow');
      writer.blankLine();

      // Stat allocation
      if (state.character.unspentStatPoints > 0) {
        writer.writeLine(`  Unspent stat points: ${state.character.unspentStatPoints}`, 'green');
        writer.blankLine();

        const statOptions: MenuOption[] = STAT_NAMES.map(stat => ({
          label: `+1 ${STAT_ABBR[stat]} (${state.character.stats[stat]} \u2192 ${state.character.stats[stat] + 1})`,
          color: 'amber' as const,
        }));

        const menuCfg = writer.menuConfig();
        const sel = await menu.show(statOptions, {
          title: 'Allocate Stat Points',
          showExit: true,
          exitLabel: 'Back (save remaining)',
          startRow: menuCfg.startRow,
          startCol: menuCfg.startCol,
          maxWidth: menuCfg.maxWidth,
        });

        if (sel === -1) return;
        if (sel >= 0 && sel < STAT_NAMES.length) {
          characterSystem.allocateStatPoint(STAT_NAMES[sel], state);
        }
        continue;
      }

      // Stat info menu
      writer.writeLine('  Press any key to continue, or...', 'khaki');
      writer.blankLine();

      const infoOptions: MenuOption[] = [
        ...STAT_NAMES.map(stat => ({
          label: `View ${STAT_ABBR[stat]} info`,
          color: 'frostSilver' as const,
        })),
        { label: 'View Vitals info', color: 'amber' as const },
      ];

      const menuCfg = writer.menuConfig();
      const sel = await menu.show(infoOptions, {
        showExit: true,
        exitLabel: 'Back',
        startRow: menuCfg.startRow,
        startCol: menuCfg.startCol,
        maxWidth: menuCfg.maxWidth,
        onBeforeRender: () => {
          ctx.sprites.hide();
          frameRenderer.drawFrame({
            title: 'STATS',
            headerRight: `Lv.${state.character.level}`,
            footer,
            borderColor: 'frostBlue',
          });
        },
      });

      if (sel === -1) return;
      if (sel >= 0 && sel < STAT_NAMES.length) {
        const stat = STAT_NAMES[sel];
        await ctx.modalManager.showTooltip(STAT_TOOLTIPS[stat]);
        continue;
      } else if (sel === STAT_NAMES.length) {
        await ctx.modalManager.showTooltip(VITALS_TOOLTIP);
        continue;
      }

      return;
    }
  }

  // ─── Inventory Tab ───────────────────────────────────────

  private async showInventory(engine: GameEngine, state: GameState): Promise<void> {
    const ctx = engine.getContext();
    const { menu, effects, frameRenderer } = ctx;

    while (true) {
      ctx.sprites.hide();
      const footer = `Items: ${state.inventory.length}  |  Gold: ${state.gold}`;

      const layout = frameRenderer.drawFrame({
        title: 'INVENTORY & EQUIPMENT',
        footer,
        borderColor: 'frostBlue',
      });

      const writer = frameRenderer.createWriter(layout.content, effects);
      writer.blankLine();

      // Equipped items
      writer.writeLine('  Equipped:', 'frostSilver');
      const slots: Array<{ slot: 'weapon' | 'armor' | 'accessory'; label: string }> = [
        { slot: 'weapon', label: 'Weapon' },
        { slot: 'armor', label: 'Armor' },
        { slot: 'accessory', label: 'Accessory' },
      ];

      for (const { slot, label } of slots) {
        const itemId = state.equipped[slot];
        if (itemId) {
          const item = getItem(itemId);
          const name = item?.name ?? itemId;
          const rarity = item?.rarity ?? 'common';
          writer.writeLine(`    ${label}: ${name}`, this.getRarityColor(rarity));
        } else {
          writer.writeLine(`    ${label}: [empty]`, 'charcoal');
        }
      }

      writer.blankLine();

      // Inventory list
      if (state.inventory.length === 0) {
        writer.blankLine();
        const offset = writer.calculateVerticalOffset(4);
        writer.skip(offset);
        writer.writeCentered('Your pack is empty.', 'white');
        writer.blankLine();
        writer.writeCentered('Items you collect will appear here.', 'khaki');
        await writer.pressAnyKey();
        return;
      }

      writer.writeLine('  Inventory:', 'frostSilver');
      const itemOptions: MenuOption[] = state.inventory.map(id => {
        const item = getItem(id);
        const name = item?.name ?? id;
        const rarity = item?.rarity ?? 'common';
        return {
          label: name,
          color: this.getRarityColor(rarity),
          suffix: item?.type ? `(${item.type})` : undefined,
          suffixColor: 'frostSilver' as const,
        };
      });

      const menuCfg = writer.menuConfig();
      const sel = await menu.show(itemOptions, {
        showExit: true,
        exitLabel: 'Back',
        startRow: menuCfg.startRow,
        startCol: menuCfg.startCol,
        maxWidth: menuCfg.maxWidth,
        onBeforeRender: () => {
          ctx.sprites.hide();
          frameRenderer.drawFrame({
            title: 'INVENTORY & EQUIPMENT',
            footer,
            borderColor: 'frostBlue',
          });
        },
      });

      if (sel === -1) return;

      // Show item detail / equip option
      if (sel >= 0 && sel < state.inventory.length) {
        await this.showItemDetail(engine, state, state.inventory[sel]);
      }
    }
  }

  private async showItemDetail(engine: GameEngine, state: GameState, itemId: string): Promise<void> {
    const ctx = engine.getContext();
    const { menu, effects, characterSystem, frameRenderer } = ctx;
    const item = getItem(itemId);

    ctx.sprites.hide();
    const layout = frameRenderer.drawFrame({
      title: item?.name ?? itemId,
      headerRight: item?.rarity ?? '',
      borderColor: 'frostBlue',
    });

    const writer = frameRenderer.createWriter(layout.content, effects);
    writer.blankLine();
    writer.writeLine(`  ${item?.name ?? itemId}`, 'icyWhite');
    if (item) {
      writer.writeLine(`  ${item.type} | ${item.rarity}`, 'frostSilver');
      writer.writeLine(`  ${item.description}`, 'white');
    }
    writer.blankLine();

    const options: MenuOption[] = [];
    // Determine if equippable
    if (item?.type === 'weapon' || item?.type === 'armor' || item?.type === 'accessory') {
      options.push({ label: `Equip as ${item.type}`, color: 'green' });
    }

    if (options.length === 0) {
      await writer.pressAnyKey();
      return;
    }

    const menuCfg = writer.menuConfig();
    const sel = await menu.show(options, {
      showExit: true,
      exitLabel: 'Back',
      startRow: menuCfg.startRow,
      startCol: menuCfg.startCol,
      maxWidth: menuCfg.maxWidth,
    });

    if (sel === 0 && item) {
      // Check class restrictions before equipping
      if (item.classRestrictions && item.classRestrictions.length > 0 &&
          !item.classRestrictions.includes(state.character.classId)) {
        writer.blankLine();
        writer.writeLine(`  Your class cannot equip ${item.name}.`, 'red');
        await effects.delay(1500);
      } else {
        const slot = item.type as 'weapon' | 'armor' | 'accessory';
        characterSystem.equipItem(itemId, slot, state);
        writer.blankLine();
        writer.writeLine(`  Equipped ${item.name}.`, 'green');
        await effects.delay(1000);
      }
    }
  }

  private getRarityColor(rarity: string): 'white' | 'green' | 'blue' | 'magenta' | 'yellow' {
    switch (rarity) {
      case 'uncommon': return 'green';
      case 'rare': return 'blue';
      case 'very_rare': return 'magenta';
      case 'legendary': return 'yellow';
      default: return 'white';
    }
  }

  // ─── Traits Tab ──────────────────────────────────────────

  private async showTraits(engine: GameEngine, state: GameState): Promise<void> {
    const ctx = engine.getContext();
    const { effects, frameRenderer } = ctx;

    ctx.sprites.hide();
    const layout = frameRenderer.drawFrame({
      title: 'TRAITS',
      headerRight: `${state.traits.size} traits`,
      borderColor: 'frostBlue',
    });

    const writer = frameRenderer.createWriter(layout.content, effects);
    writer.blankLine();

    if (state.traits.size === 0) {
      writer.autoCenterIfSparse(5);
      writer.writeCentered('No traits acquired yet.', 'frostSilver');
      writer.blankLine();
      writer.writeLine('  Traits develop based on your choices.', 'khaki');
      writer.writeLine('  Make bold or cautious decisions to shape', 'khaki');
      writer.writeLine('  your character\'s personality.', 'khaki');
    } else {
      for (const [, trait] of state.traits) {
        const color = this.getTraitColor(trait.category);
        writer.writeLine(`  ${trait.name} (Lv.${trait.level})`, color);
        if (trait.description) {
          writer.writeLine(`    ${trait.description}`, 'white');
        }
      }
    }

    writer.blankLine();
    await writer.pressAnyKey();
  }

  private getTraitColor(category: string): 'frostBlue' | 'yellow' | 'green' | 'magenta' {
    switch (category) {
      case 'personality': return 'frostBlue';
      case 'approach': return 'yellow';
      case 'reputation': return 'green';
      case 'transformation': return 'magenta';
      default: return 'frostBlue';
    }
  }

  // ─── Relationships Tab ───────────────────────────────────

  private async showRelationships(engine: GameEngine, state: GameState): Promise<void> {
    const ctx = engine.getContext();
    const { menu, effects, characterSystem, frameRenderer } = ctx;

    while (true) {
      ctx.sprites.hide();
      const layout = frameRenderer.drawFrame({
        title: 'RELATIONSHIPS',
        headerRight: `${state.relationships.size} NPCs`,
        borderColor: 'frostBlue',
      });

      const writer = frameRenderer.createWriter(layout.content, effects);
      writer.blankLine();

      if (state.relationships.size === 0) {
        writer.autoCenterIfSparse(5);
        writer.writeCentered('No relationships established yet.', 'frostSilver');
        writer.blankLine();
        writer.writeLine('  NPCs you meet will appear here.', 'khaki');
        writer.writeLine('  Your choices affect how they regard you.', 'khaki');
      } else {
        for (const [npcId, score] of state.relationships) {
          const tier = characterSystem.getRelationshipTier(score);
          const npc = getNPC(npcId);
          const name = npc?.name ?? npcId;
          const color = this.getTierColor(tier);
          writer.writeLine(`  ${name}: ${score} (${tier})`, color);
        }
      }

      writer.blankLine();
      writer.writeLine('  Press any key to continue, or...', 'khaki');
      writer.blankLine();

      const menuCfg = writer.menuConfig();
      const sel = await menu.show([
        { label: 'View Relationship Tiers info', color: 'amber' as const },
      ], {
        showExit: true,
        exitLabel: 'Back',
        startRow: menuCfg.startRow,
        startCol: menuCfg.startCol,
        maxWidth: menuCfg.maxWidth,
        onBeforeRender: () => {
          ctx.sprites.hide();
          frameRenderer.drawFrame({
            title: 'RELATIONSHIPS',
            headerRight: `${state.relationships.size} NPCs`,
            borderColor: 'frostBlue',
          });
        },
      });

      if (sel === -1) return;
      if (sel === 0) {
        await ctx.modalManager.showTooltip(RELATIONSHIP_TIER_TOOLTIP);
        continue;
      }

      return;
    }
  }

  private getTierColor(tier: string): 'green' | 'frostBlue' | 'white' | 'yellow' | 'red' {
    switch (tier) {
      case 'Allied': return 'green';
      case 'Friendly': return 'frostBlue';
      case 'Neutral': return 'white';
      case 'Distrustful': return 'yellow';
      case 'Hostile': return 'red';
      default: return 'white';
    }
  }

  // ─── Quests Tab ──────────────────────────────────────────

  private async showQuests(engine: GameEngine, state: GameState): Promise<void> {
    const ctx = engine.getContext();
    const { effects, frameRenderer } = ctx;

    ctx.sprites.hide();
    const layout = frameRenderer.drawFrame({
      title: 'QUESTS',
      headerRight: `${state.quests.size} quests`,
      borderColor: 'frostBlue',
    });

    const writer = frameRenderer.createWriter(layout.content, effects);
    writer.blankLine();

    if (state.quests.size === 0) {
      writer.autoCenterIfSparse(7);
      writer.writeCentered('═══ NO ACTIVE QUESTS ═══', 'runeGold');
      writer.blankLine();
      writer.writeLine('  Quests are discovered as you explore the world of', 'frostSilver');
      writer.writeLine('  the Greyveil. Make choices, follow leads, and speak', 'frostSilver');
      writer.writeLine('  with NPCs to uncover new objectives.', 'frostSilver');
      writer.blankLine();
      writer.writeLine('  Continue your adventure to find your first quest.', 'white');
    } else {
      // Group by status
      const active: Array<[string, string]> = [];
      const completed: Array<[string, string]> = [];
      const failed: Array<[string, string]> = [];

      for (const [id, status] of state.quests) {
        if (status === 'completed') completed.push([id, status]);
        else if (status === 'failed') failed.push([id, status]);
        else active.push([id, status]);
      }

      if (active.length > 0) {
        writer.writeLine('  Active:', 'yellow');
        for (const [id] of active) {
          writer.writeLine(`    \u2022 ${id}`, 'yellow');
        }
        writer.blankLine();
      }

      if (completed.length > 0) {
        writer.writeLine('  Completed:', 'green');
        for (const [id] of completed) {
          writer.writeLine(`    \u2713 ${id}`, 'green');
        }
        writer.blankLine();
      }

      if (failed.length > 0) {
        writer.writeLine('  Failed:', 'red');
        for (const [id] of failed) {
          writer.writeLine(`    \u2717 ${id}`, 'red');
        }
        writer.blankLine();
      }
    }

    await writer.pressAnyKey();
  }

  // ─── Save Menu ───────────────────────────────────────────

  private async showSaveMenu(engine: GameEngine, state: GameState): Promise<void> {
    const ctx = engine.getContext();
    const { menu, prompt, effects, saveSystem, frameRenderer } = ctx;
    const cls = getClass(state.character.classId);

    while (true) {
      ctx.sprites.hide();
      const layout = frameRenderer.drawFrame({
        title: 'SAVE GAME',
        borderColor: 'frostBlue',
      });

      const writer = frameRenderer.createWriter(layout.content, effects);
      writer.blankLine();

      const slots = saveSystem.getAllSaveSlots();
      const options: MenuOption[] = slots.map((meta, i) => {
        if (meta) {
          const label = i === 0 ? '[Auto]' : `Slot ${i}`;
          const playtime = formatPlaytime(meta.playtime);
          const date = formatDate(meta.savedAt);
          return {
            label: `${label} ${meta.characterName}, ${meta.className} Lv.${meta.level}`,
            color: 'white' as const,
            suffix: `${playtime} | ${date}`,
            suffixColor: 'frostSilver' as const,
          };
        }
        const label = i === 0 ? '[Auto] Empty' : `Slot ${i}: [Empty]`;
        return { label, color: 'charcoal' as const };
      });

      const menuCfg = writer.menuConfig();
      const sel = await menu.show(options, {
        showExit: true,
        exitLabel: 'Back',
        startRow: menuCfg.startRow,
        startCol: menuCfg.startCol,
        maxWidth: menuCfg.maxWidth,
        onBeforeRender: () => {
          ctx.sprites.hide();
          frameRenderer.drawFrame({
            title: 'SAVE GAME',
            borderColor: 'frostBlue',
          });
        },
      });

      if (sel === -1) return;

      if (sel === 0) {
        // Auto-save slot — overwrite without confirmation
        const node = ctx.storyEngine.getCurrentNode();
        saveSystem.save(0, state, node?.title ?? '', cls.name);
        writer.blankLine();
        writer.writeLine('  Auto-save updated.', 'green');
        await effects.delay(1000);
        continue;
      }

      if (sel >= 1 && sel < MAX_SAVE_SLOTS) {
        const existing = slots[sel];
        if (existing) {
          const overwrite = await prompt.confirm(`Overwrite save in Slot ${sel}?`, false);
          if (!overwrite) continue;
        }
        const node = ctx.storyEngine.getCurrentNode();
        saveSystem.save(sel, state, node?.title ?? '', cls.name);

        // Show save confirmation
        ctx.sprites.hide();
        const confirmLayout = frameRenderer.drawFrame({
          title: 'SAVE GAME',
          borderColor: 'frostBlue',
        });
        const confirmWriter = frameRenderer.createWriter(confirmLayout.content, effects);
        const offset = confirmWriter.calculateVerticalOffset(3);
        confirmWriter.skip(offset);
        confirmWriter.writeCentered(`Saved to Slot ${sel}.`, 'green');
        await effects.delay(1000);
      }
    }
  }
}

// ─── Utility Functions ─────────────────────────────────────

export function formatPlaytime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export function formatDate(isoString: string): string {
  try {
    const d = new Date(isoString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[d.getMonth()]} ${d.getDate()}`;
  } catch {
    return '';
  }
}
